import { useT, userError } from '../i18n/messages';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { PageHeader } from '../components/PageHeader';
import { Button, Card } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { ApiError, billingApi, type CustomerTimelineResponse } from '../lib/api';

function text(value: unknown, fallback = '—') {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function describeError(error: unknown) {
  return userError(error);
}

export function CustomerTimelinePage() {
  const t = useT();
  const { customerId } = useParams<{ customerId: string }>();
  const { tokens } = useAuth();
  const [state, setState] = useState<{
    status: 'loading' | 'success' | 'error';
    data: CustomerTimelineResponse | null;
    error: ApiError | null;
  }>({ status: 'loading', data: null, error: null });

  async function load(signal?: AbortSignal) {
    if (!tokens || !customerId) {
      return;
    }
    setState({ status: 'loading', data: null, error: null });
    try {
      const data = await billingApi.getCustomerTimeline(tokens.access_token, customerId, signal);
      setState({ status: 'success', data, error: null });
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      setState({
        status: 'error',
        data: null,
        error:
          error instanceof ApiError
            ? error
            : new ApiError(describeError(error), 500, 'request_failed'),
      });
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [customerId, tokens?.access_token]);

  if (!tokens) {
    return (
      <section className="panel page-panel">
        {t('billing.ui.your.session.is.missing.or.expired')}
      </section>
    );
  }

  if (!customerId) {
    return <section className="panel page-panel">{t('billing.ui.customer.id.is.missing')}</section>;
  }

  if (state.status === 'loading') {
    return <section className="panel page-panel">{t('billing.ui.loading.data')}</section>;
  }

  if (state.status === 'error' || !state.data) {
    return (
      <Card className="panel panel-error page-panel">
        <p>{userError(state.error, t)}</p>
        <Button onClick={() => void load()} type="button">
          {t('billing.resource.retry')}
        </Button>
      </Card>
    );
  }

  return <CustomerTimelineContent data={state.data} />;
}

export function CustomerTimelineContent({ data }: { data: CustomerTimelineResponse }) {
  const t = useT();
  const customer = data.customer;
  const applications = Array.isArray(customer.applications)
    ? (customer.applications as Array<Record<string, unknown>>)
    : [];

  return (
    <section className="page" data-testid="billing-customer-timeline-page">
      <PageHeader title={t('billing.ui.customer.timeline')} />
      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{text(customer.primary_email_snapshot, t('billing.ui.customer'))}</h3>
          <span className="detail-badge">{text(customer.status)}</span>
        </div>
        <dl className="detail-list">
          <div>
            <dt>{t('billing.ui.customer.id')}</dt>
            <dd>{text(customer.id)}</dd>
          </div>
          <div>
            <dt>{t('billing.column.displayName')}</dt>
            <dd>{text(customer.display_name_snapshot)}</dd>
          </div>
          <div>
            <dt>{t('billing.nav.applications')}</dt>
            <dd>
              {applications.length === 0
                ? '—'
                : applications
                    .map(
                      (application) =>
                        `${text(application.application_key)}:${text(application.application_user_id)}`,
                    )
                    .join(', ')}
            </dd>
          </div>
          <div>
            <dt>{t('billing.ui.generated')}</dt>
            <dd>{data.generated_at}</dd>
          </div>
        </dl>
      </Card>

      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.ui.chronology')}</h3>
          <span className="detail-badge">
            {t('billing.timeline.records', { count: data.items.length })}
          </span>
        </div>
        {data.items.length === 0 ? (
          <p>{t('billing.ui.no.customer.activity.was.found')}</p>
        ) : (
          <ol className="timeline-list">
            {data.items.map((item) => (
              <li className={`timeline-item timeline-item--${item.category}`} key={item.id}>
                <div className="timeline-item__marker" aria-hidden="true" />
                <div className="timeline-item__body">
                  <div className="panel-heading">
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.description}</p>
                    </div>
                    <span className="detail-badge">{item.category}</span>
                  </div>
                  <dl className="detail-list">
                    <div>
                      <dt>{t('billing.ui.occurred')}</dt>
                      <dd>{item.occurred_at}</dd>
                    </div>
                    <div>
                      <dt>{t('billing.column.status')}</dt>
                      <dd>{item.status ?? '—'}</dd>
                    </div>
                  </dl>
                  {item.related_path ? (
                    <Link className="inline-link" to={item.related_path}>
                      {t('billing.ui.open.related.record')}
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </section>
  );
}
